import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { FileUploaderComponent } from '../../../../../shared/components/file-uploader/file-uploader.component';
import { DialogUploadAndCroppingComponent } from '../../../../new-project/components/dialog-upload-and-cropping/dialog-upload-and-cropping.component';
import { base64ToFile } from 'ngx-image-cropper';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment/moment';
import 'moment/locale/ru';
import 'moment/locale/ar';
import { WeeklyReportService } from '../../../../../services/WeeklyReports/weekly-report.service';
import { ToastrService } from 'ngx-toastr';
import { PhaseData, WeeklyReportDetail } from '../../../../../models/weeklyReports.model';
import { UserService } from '../../../../../core/user/user.service';

@Component({
  selector: 'app-dialog-weekly-report',
  templateUrl: './dialog-weekly-report.component.html',
  styleUrls: ['./dialog-weekly-report.component.scss'],
})
export class DialogWeeklyReportComponent implements OnInit {
  reportForm: FormGroup;
  endDate: any = '';
  @ViewChild('fileUploader') fileUploader: FileUploaderComponent;
  fileOptions = {
    maxFilesLimit: 1,
    maxFileSize: 200,
    allowedExtensions: ['jpg', 'jpeg', 'png'],
  };
  attachment: File[];
  croppedImage: any;
  lastPhase: PhaseData;
  reportDetails: WeeklyReportDetail;
  isLastPhaseLoading: boolean = true;
  isGenerating: boolean = false;
  timestamp: any;
  financialInputType: 'sar' | 'percentage' = 'sar'; // Default to SAR
  nameFieldTouched: boolean = false; // Track if name field has been manually interacted with
  hasUserTypedInName: boolean = false; // Track if user has ever typed in name field
  isNwcClient: boolean = false; // Track if current user is NWC client

  // Custom validator for trimmed required field
  static trimmedRequired(control: AbstractControl): ValidationErrors | null {
    if (!control.value || control.value.trim().length === 0) {
      return { required: true };
    }
    return null;
  }

  // Custom validator for trimmed max length
  static trimmedMaxLength(maxLength: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      const trimmedLength = control.value.trim().length;
      if (trimmedLength > maxLength) {
        return { maxlength: { requiredLength: maxLength, actualLength: trimmedLength } };
      }
      return null;
    };
  }

  // Get trimmed length for display
  getTrimmedNameLength(): number {
    const nameValue = this.reportForm?.get('name')?.value;
    return nameValue ? nameValue.trim().length : 0;
  }

  // Cache original values for both input types in edit mode
  originalFinancialValues: {
    sar: { actual_cost: any; remaining_cost: any; pending_cost: any };
    percentage: { actual_cost: any; remaining_cost: any; pending_cost: any };
  } = {
    sar: { actual_cost: null, remaining_cost: null, pending_cost: null },
    percentage: { actual_cost: null, remaining_cost: null, pending_cost: null },
  };

  constructor(
    public dialogRef: MatDialogRef<DialogWeeklyReportComponent>,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    public _toaster: ToastrService,
    private translocoService: TranslocoService,
    public weeklyReportService: WeeklyReportService,
    private userService: UserService,
    @Inject(MAT_DIALOG_DATA)
    public data: { projectId: string; totalCost: string; reportId: string },
  ) {}

  async ngOnInit(): Promise<void> {
    this.timestamp = new Date().getTime();
    // Check if user is NWC client
    this.isNwcClient = this.userService.isNwcClientSync();
    this.initForm();
    this.getLastPhaseDetails();
    if (this.data.reportId) {
      this.getWeeklyReportDetails();
    }
  }

  private getLastPhaseDetails() {
    this.weeklyReportService.getLastPhaseDetails(this.data.projectId).subscribe(
      (response) => {
        this.isLastPhaseLoading = true;
        this.lastPhase = response.data;
        if (this.lastPhase.project_completion_percentage) {
          this.reportForm
            .get('actual_progress_percentage')
            .setValue(this.lastPhase.project_completion_percentage);
        }
        if (
          this.lastPhase.project_cost.actual_cost !== undefined &&
          this.lastPhase.project_cost.actual_cost !== null
        ) {
          this.reportForm.get('actual_cost').setValue(this.lastPhase.project_cost.actual_cost);
        }
        if (
          this.lastPhase.project_cost.differenceCost !== undefined &&
          this.lastPhase.project_cost.differenceCost !== null
        ) {
          this.reportForm
            .get('remaining_cost')
            .setValue(this.lastPhase.project_cost.differenceCost);
        }
        this.isLastPhaseLoading = false;
      },
      (error) => {
        this.isLastPhaseLoading = false;

        // Check if the error has the expected structure
        error.error.errors.forEach((errorMessage: string) => {
          /*no need to toast this error as this is optional field*/
          // this._toaster.error(errorMessage);
        });
      },
    );
  }

  private getWeeklyReportDetails() {
    this.weeklyReportService
      .getWeeklyReportDetails(this.data.projectId, this.data.reportId)
      .subscribe(
        (response) => {
          this.reportDetails = response.data;
          if (this.reportDetails) {
            this.reportForm.get('name').setValue(this.reportDetails.name);
            this.reportForm.get('actual_cost').setValue(this.data.totalCost);
            this.reportForm.get('start_date').setValue(new Date(this.reportDetails.start_date));
            const endDateObj = new Date(this.reportDetails.end_date);
            this.reportForm.get('end_date').setValue(endDateObj);
            this.endDate = this.formatEndDateForDisplay(endDateObj);
            this.reportForm
              .get('planned_progress_percentage')
              .setValue(this.reportDetails.planned_progress_percentage);
            this.reportForm
              .get('actual_progress_percentage')
              .setValue(this.reportDetails.actual_progress_percentage);

            if (this.reportDetails.pending_cost) {
              this.reportForm.get('pending_cost').setValue(this.reportDetails.pending_cost);
            }

            if (this.reportDetails.actual_cost) {
              this.reportForm.get('actual_cost').setValue(this.reportDetails.actual_cost);
            }

            if (this.reportDetails.remaining_cost) {
              this.reportForm.get('remaining_cost').setValue(this.reportDetails.remaining_cost);
            }

            // Set financial input type if available
            if (this.reportDetails['financial_input_type']) {
              this.financialInputType = this.reportDetails['financial_input_type'];
              this.reportForm
                .get('financial_input_type')
                .setValue(this.financialInputType, { emitEvent: false });

              // Cache the original values based on the financial input type
              const inputType = this.financialInputType;
              this.originalFinancialValues[inputType] = {
                actual_cost: this.reportDetails.actual_cost,
                remaining_cost: this.reportDetails.remaining_cost,
                pending_cost: this.reportDetails.pending_cost,
              };

              // Apply validators based on the loaded financial input type
              this.applyFinancialValidators();
            }

            // Add achievements
            if (this.reportDetails.achievements_description) {
              this.reportDetails.achievements_description.forEach((achievement) => {
                this.achievements.push(
                  this.formBuilder.group({
                    date: [new Date(achievement.date), Validators.required],
                    status: [achievement.status, Validators.required],
                    description: [achievement.description, Validators.required],
                  }),
                );
              });
            }

            // Add challenges
            if (this.reportDetails.challenges_description) {
              this.reportDetails.challenges_description.forEach((challenge) => {
                this.challenges.push(
                  this.formBuilder.group({
                    date: [new Date(challenge.date), Validators.required],
                    actionNeeded: [challenge.actionNeeded, Validators.required],
                    description: [challenge.description, Validators.required],
                    status: [challenge.status, Validators.required],
                  }),
                );
              });
            }
          }
        },
        (error) => {
          // Check if the error has the expected structure
          error.error.errors.forEach((errorMessage: string) => {
            this._toaster.error(errorMessage);
          });
        },
      );
  }

  initForm(): void {
    this.reportForm = this.formBuilder.group({
      name: [
        '',
        [
          DialogWeeklyReportComponent.trimmedRequired,
          DialogWeeklyReportComponent.trimmedMaxLength(255),
        ],
      ],
      start_date: ['', Validators.required],
      end_date: ['', Validators.required],
      actual_progress_percentage: ['', Validators.required],
      planned_progress_percentage: ['100', Validators.required],
      pending_cost: ['', [Validators.min(0)]],
      actual_cost: ['', [Validators.required, Validators.min(0)]],
      remaining_cost: ['', [Validators.required, Validators.min(0)]],
      deviation: [''],
      financial_input_type: ['sar'], // Add financial input type field
      achievements_description: this.formBuilder.array([]),
      challenges_description: this.formBuilder.array([]),
    });
    this.reportForm.get('start_date').valueChanges.subscribe((value) => {
      this.calculateEndDate(value);
    });

    // Listen to financial input type changes
    this.reportForm
      .get('financial_input_type')
      .valueChanges.subscribe((value: 'sar' | 'percentage') => {
        this.onFinancialInputTypeChange(value);
      });

    // Track when user actually starts typing in the name field
    this.reportForm.get('name').valueChanges.subscribe((value) => {
      if (value && value.length > 0) {
        this.hasUserTypedInName = true;
        this.nameFieldTouched = true;
      }
    });
  }

  onNameFieldBlur(): void {
    // Only mark as touched if user has actually typed something
    // This prevents showing error on first blur from auto-focus
    if (this.hasUserTypedInName) {
      this.nameFieldTouched = true;
      this.reportForm.get('name').markAsTouched();
    } else {
      // Reset touched state on first blur to prevent red border
      this.reportForm.get('name').markAsUntouched();
    }
  }

  onFinancialInputTypeChange(newType: 'sar' | 'percentage'): void {
    // Save current values before switching (for user-entered values)
    const currentType = this.financialInputType;
    const actualCostValue = this.reportForm.get('actual_cost').value;
    const remainingCostValue = this.reportForm.get('remaining_cost').value;
    const pendingCostValue = this.reportForm.get('pending_cost').value;

    // Only save if user has entered values (not empty)
    if (actualCostValue !== '' && actualCostValue !== null) {
      this.originalFinancialValues[currentType].actual_cost = actualCostValue;
    }
    if (remainingCostValue !== '' && remainingCostValue !== null) {
      this.originalFinancialValues[currentType].remaining_cost = remainingCostValue;
    }
    if (pendingCostValue !== '' && pendingCostValue !== null) {
      this.originalFinancialValues[currentType].pending_cost = pendingCostValue;
    }

    // Update the component property to match the form control
    this.financialInputType = newType;

    // Restore cached values for the new type if they exist, otherwise clear
    const cachedValues = this.originalFinancialValues[newType];
    this.reportForm
      .get('actual_cost')
      .setValue(cachedValues.actual_cost !== null ? cachedValues.actual_cost : '', {
        emitEvent: false,
      });
    this.reportForm
      .get('remaining_cost')
      .setValue(cachedValues.remaining_cost !== null ? cachedValues.remaining_cost : '', {
        emitEvent: false,
      });
    this.reportForm
      .get('pending_cost')
      .setValue(cachedValues.pending_cost !== null ? cachedValues.pending_cost : '', {
        emitEvent: false,
      });

    this.applyFinancialValidators();
  }

  applyFinancialValidators(): void {
    // Update validators based on the financial input type
    const actualCostControl = this.reportForm.get('actual_cost');
    const remainingCostControl = this.reportForm.get('remaining_cost');
    const pendingCostControl = this.reportForm.get('pending_cost');

    if (this.financialInputType === 'percentage') {
      // Add percentage validators (0-100)
      actualCostControl.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(100),
      ]);
      remainingCostControl.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(100),
      ]);
      pendingCostControl.setValidators([Validators.min(0), Validators.max(100)]);

      // Clamp existing values to 0-100 range
      if (actualCostControl.value > 100) {
        actualCostControl.setValue(100, { emitEvent: false });
      }
      if (remainingCostControl.value > 100) {
        remainingCostControl.setValue(100, { emitEvent: false });
      }
      if (pendingCostControl.value > 100) {
        pendingCostControl.setValue(100, { emitEvent: false });
      }
    } else {
      // SAR validators (only required for actual and remaining)
      actualCostControl.setValidators([Validators.required, Validators.min(0)]);
      remainingCostControl.setValidators([Validators.required, Validators.min(0)]);
      pendingCostControl.setValidators([Validators.min(0)]);
    }

    // Update validation state without emitting events or marking as touched
    actualCostControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    remainingCostControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    pendingCostControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  onFinancialInputChange(controlName: string, event: any): void {
    if (this.financialInputType === 'percentage') {
      const value = parseFloat(event.target.value);
      if (value > 100) {
        this.reportForm.get(controlName).setValue(100);
      } else if (value < 0) {
        this.reportForm.get(controlName).setValue(0);
      }
    }
  }

  onNoClick() {
    this.dialogRef.close();
  }

  private formatEndDateForDisplay(date: Date): string {
    const activeLang = this.translocoService.getActiveLang() || 'en';
    const momentLocale = activeLang === 'ar' ? 'ar' : activeLang === 'ru' ? 'ru' : 'en';
    const displayFormat =
      activeLang === 'ru' ? 'DD.MM.YYYY' : activeLang === 'ar' ? 'DD/MM/YYYY' : 'M/D/YYYY';
    return moment(date).locale(momentLocale).format(displayFormat);
  }

  calculateEndDate(startDate: Date): void {
    if (startDate) {
      const endDateMoment = moment(startDate).add(7, 'days');
      this.endDate = this.formatEndDateForDisplay(endDateMoment.toDate());
      this.reportForm.get('end_date').setValue(endDateMoment.toDate());
    }
  }

  onStartDateChange(date: Date): void {
    this.reportForm.get('start_date').setValue(date);
  }

  get achievements(): FormArray {
    return this.reportForm.get('achievements_description') as FormArray;
  }

  get challenges(): FormArray {
    return this.reportForm.get('challenges_description') as FormArray;
  }

  addAchievement(): void {
    this.achievements.push(
      this.formBuilder.group({
        date: ['', Validators.required],
        status: ['', Validators.required],
        description: ['', Validators.required],
      }),
    );
  }

  removeAchievement(index: number): void {
    this.achievements.removeAt(index);
  }

  addChallenges() {
    this.challenges.push(
      this.formBuilder.group({
        date: ['', Validators.required],
        actionNeeded: ['', Validators.required],
        description: ['', Validators.required],
        status: ['', Validators.required],
      }),
    );
  }

  removeChallenges(index: number): void {
    this.challenges.removeAt(index);
  }

  openImageDialog(): void {
    const dialogRef = this.dialog.open(DialogUploadAndCroppingComponent, {
      width: '450px',
      data: {
        title: this.translocoService.translate('Upload deviation graph'),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.croppedImage = result;
        if (this.reportDetails?.deviation) {
          this.reportDetails.deviation = undefined;
        }
      }
    });
  }

  removeImg(): void {
    this.croppedImage = undefined;
  }

  formDataPrepare(update?: boolean): any {
    if (this.reportForm.valid) {
      // Trim the name field before submission
      const nameValue = this.reportForm.get('name').value;
      if (nameValue) {
        this.reportForm.get('name').setValue(nameValue.trim(), { emitEvent: false });
      }

      this.reportForm
        .get('start_date')
        .setValue(
          moment(this.reportForm.get('start_date').value).locale('en').format('YYYY-MM-DD'),
        );
      this.reportForm
        .get('end_date')
        .setValue(moment(this.reportForm.get('end_date').value).locale('en').format('YYYY-MM-DD'));

      const FORM_BODY = new FormData();
      Object.keys(this.reportForm.controls).forEach((control) => {
        // Skip financial_input_type for non-NWC clients
        if (control === 'financial_input_type' && !this.isNwcClient) {
          return;
        }

        if (control === 'achievements_description' || control === 'challenges_description') {
          // Send the arrays as multiple form fields
          this.reportForm.get(control).value.forEach((item, index) => {
            Object.keys(item).forEach((key) => {
              FORM_BODY.append(`${control}[${index}][${key}]`, item[key]);
            });
          });
          if (this.reportForm.get(control).value.length == 0 && update) {
            FORM_BODY.append(`${control}`, null);
          }
        } else {
          if (this.reportForm.controls[control]?.value !== '') {
            FORM_BODY.append(control, this.reportForm.controls[control]?.value);
          }
        }
      });
      if (this.croppedImage) {
        this.reportForm.get('deviation').setValue(base64ToFile(this.croppedImage));
        FORM_BODY.append('deviation', this.reportForm.get('deviation').value ?? '');
      }
      return FORM_BODY;
    }
  }

  submitReport() {
    if (this.reportForm.valid) {
      this.weeklyReportService
        .createWeeklyReport(this.formDataPrepare(), this.data.projectId)
        .subscribe(
          (res) => {
            this.isGenerating = true;
            if (res) {
              this._toaster.success(
                this.translocoService.translate('Weekly report generated successfully'),
              );
            }
            this.isGenerating = false;
            this.dialogRef.close(true);
          },
          (error) => {
            error.error.errors.forEach((errorMessage: string) => {
              this._toaster.error(errorMessage);
            });
            this.isGenerating = false;
          },
        );
    }
  }

  updateReport() {
    if (this.reportForm.valid) {
      this.weeklyReportService
        .updateWeeklyReport(this.formDataPrepare(true), this.data.projectId, this.data.reportId)
        .subscribe(
          (res) => {
            this.isGenerating = true;
            if (res) {
              this._toaster.success(
                this.translocoService.translate('Weekly report updated successfully'),
              );
            }
            this.isGenerating = false;
            this.dialogRef.close(true);
          },
          (error) => {
            error.error.errors.forEach((errorMessage: string) => {
              this._toaster.error(errorMessage);
            });
            this.isGenerating = false;
          },
        );
    }
  }
}
